import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import { useAuth } from '../contexts/AuthContext';
import './Reports.css';

const API_BASE_URL = 'http://72.61.235.16:5001/api/v1';

const Reports = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data.data.reports || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = (report) => {
    navigate(`/reports/${report.id}`, { state: { report } });
  };

  const handleDownloadReport = async (report) => {
    try {
      const pdf = new jsPDF();
      
      // Add title
      pdf.setFontSize(20);
      pdf.setTextColor(17, 74, 166);
      pdf.text('Site Report', 20, 30);
      
      // Add project info
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Project: ${report.project_name || 'N/A'}`, 20, 50);
      pdf.text(`Location: ${report.project_location || 'N/A'}`, 20, 65);
      pdf.text(`Created: ${new Date(report.created_at).toLocaleDateString()}`, 20, 80);
      
      // Add report details
      pdf.setFontSize(14);
      pdf.text('Report Details:', 20, 105);
      
      pdf.setFontSize(12);
      pdf.text(`Total Floors: ${report.total_floors}`, 25, 120);
      pdf.text(`Dimensions: ${report.dimensions}`, 25, 135);
      pdf.text(`Approval Status: ${(report.approval_status || 'pending').toUpperCase()}`, 25, 150);
      
      if (report.remarks) {
        pdf.text('Remarks:', 25, 170);
        const splitRemarks = pdf.splitTextToSize(report.remarks, 160);
        pdf.text(splitRemarks, 25, 185);
      }
      
      // Add images info if available
      if (report.images && report.images.length > 0) {
        const yPos = report.remarks ? 210 : 170;
        pdf.text(`Site Images: ${report.images.length} image(s) available`, 25, yPos);
        pdf.text('(Images can be viewed in the web application)', 25, yPos + 15);
      }
      
      // Save the PDF
      pdf.save(`site-report-${report.project_name?.replace(/\s+/g, '-') || 'report'}-${Date.now()}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      case 'pending':
      default:
        return '#f59e0b';
    }
  };

  const filteredReports = reports.filter((report) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      report.project_name?.toLowerCase().includes(query) ||
      report.project_location?.toLowerCase().includes(query) ||
      report.approval_status?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="page-subtitle">View all site reports</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <i className="fas fa-search search-icon"></i>
        <input
          type="text"
          className="search-input"
          placeholder="Search by project name, location, or status..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading reports...</p>
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-file-alt empty-icon"></i>
          <p>No reports available</p>
        </div>
      ) : (
        <div className="reports-grid">
          {filteredReports.map((report) => (
            <div key={report.id} className="report-card">
              <div className="report-header">
                <div className="report-title-container">
                  <h3 className="report-title">{report.project_name || 'N/A'}</h3>
                  <p className="report-location">{report.project_location || ''}</p>
                </div>
                <div 
                  className="report-status" 
                  style={{ backgroundColor: getStatusColor(report.approval_status) }}
                >
                  {(report.approval_status || 'pending').toUpperCase()}
                </div>
              </div>

              <div className="report-details">
                <div className="report-detail">
                  <span className="report-label">Floors:</span>
                  <span className="report-value">{report.total_floors}</span>
                </div>
                <div className="report-detail">
                  <span className="report-label">Dimensions:</span>
                  <span className="report-value">{report.dimensions}</span>
                </div>
                <div className="report-detail">
                  <span className="report-label">Created:</span>
                  <span className="report-value">
                    {new Date(report.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {report.remarks && (
                <p className="report-remarks">{report.remarks}</p>
              )}

              <div className="report-actions">
                <button 
                  className="btn-report-action btn-view"
                  onClick={() => handleViewReport(report)}
                >
                  <i className="fas fa-eye"></i>
                  View
                </button>
                <button 
                  className="btn-report-action btn-download"
                  onClick={() => handleDownloadReport(report)}
                >
                  <i className="fas fa-download"></i>
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Reports;
